import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Typography,
    Box,
  } from "@mui/material";
  import { format } from "date-fns";
  import React from "react";
import { retrieveFastFilteredShippingInstructionCostingApi, retrieveFastShippingInstructionCostingApi, retrieveShippingInstructionByIdApi } from "../../../../components/api/ShippingInstructionApiService";
  
  function ViewShippingInstructionCostingDialog({ open, row, onClose }) {

    const [refTotalExclStorageUsd, setRefTotalExclStorageUsd] = React.useState(null);
    React.useEffect(() => {
        if (row && open && row?.shipmentRequestreferenceShippingInstructionId) {
              const filterShippingInstruction = ({
                shippingInstructionIdList: [row?.shipmentRequestreferenceShippingInstructionId],      
                recordStatusList: ["ACTIVE"],
              });
          retrieveFastFilteredShippingInstructionCostingApi(filterShippingInstruction)
            .then(({ data: siArray }) => {
              if (siArray && siArray.length > 0) {
                const si = siArray[0]; // Get the first element of the array
                const usdCosts = [
                  si.actualBuyingPriceUsd ? si.actualBuyingPriceUsd : si.purchaseOrderUsdFinalPrice,
                  si.actualInsuranceCostUsd,
                  si.actualFreightPriceUsd,
                  si.secondFreightCostUsd,
                  si.importExportExpenseUsd,
                  si.demurrageUsd,
                  si.fumigationUsd,
                  si.handlingUsd,
                  si.debitNoteUsd,
                  -1 * (si.creditNoteUsd || 0),
                ];
                const total = usdCosts.reduce((sum, v) => sum + (v || 0), 0);
                setRefTotalExclStorageUsd(total);
              } else {
                setRefTotalExclStorageUsd(null);
              }
            })
            .catch(() => setRefTotalExclStorageUsd(null));
        } else {
          setRefTotalExclStorageUsd(null);
        }
      }, [open, row?.shipmentRequestreferenceShippingInstructionId]);
      
    if (!row){
        return null;
    } 

    const isFW = row?.shipmentRequestType?.startsWith('FW');
    // if we have a ref‐SI total, use that; otherwise fallback to the normal PO price
    const unitBuyingPriceUsd = (isFW && refTotalExclStorageUsd != null)
      ? refTotalExclStorageUsd
      : row?.actualBuyingPriceUsd ?
        row?.actualBuyingPriceUsd
        : row?.purchaseOrderUsdFinalPrice;
    const unitBuyingPriceOriginal = (isFW && refTotalExclStorageUsd != null)
      ? /* you’ve no “original” for it, so either leave blank or copy USD: */ refTotalExclStorageUsd
      : row?.actualBuyingPrice ?
        row?.actualBuyingPrice
        : row?.purchaseOrderFinalPrice;
    const unitBuyingCurrency = (isFW && refTotalExclStorageUsd != null)
      ? 'USD'
      : row?.actualBuyingPriceCurrency ?
        row?.actualBuyingPriceCurrency
        : row?.purchaseOrderFinalPriceCurrency;
      
    // Core cost items with originals, currency, USD, and dates
    const costItems = [
        // Include Buying Price in core costs
        {
            label: isFW
            ? 'Unit Base Price From Reference'
            : 'Unit Buying Price',
                 original: unitBuyingPriceOriginal,
                 currency: unitBuyingCurrency,
                 usd: unitBuyingPriceUsd,
                 date: null
        },
        { label: 'Unit Insurance Cost', original: row?.actualInsuranceCost, currency: row?.actualInsuranceCurrency, usd: row?.actualInsuranceCostUsd, date: row?.actualTimeOfInsurance },
        { label: 'Unit Freight Cost', original: row?.actualFreightPrice, currency: row?.actualFreightCurrency, usd: row?.actualFreightPriceUsd, date: row?.actualTimeOfFreight },
        { label: 'Unit Second Freight Cost', original: row?.secondFreightCost, currency: row?.secondFreightCostCurrency, usd: row?.secondFreightCostUsd, date: row?.secondFreightDate },
        { label: 'Unit Import/Export Expenses', original: row?.importExportExpense, currency: row?.importExportExpenseCurrency, usd: row?.importExportExpenseUsd, date: row?.importExportExpenseDate },
        { label: 'Unit Demurrage', original: row?.demurrage, currency: row?.demurrageCurrency, usd: row?.demurrageUsd, date: row?.demurrageDate },
        { label: 'Unit Fumigation', original: row?.fumigation, currency: row?.fumigationCurrency, usd: row?.fumigationUsd, date: row?.fumigationDate },
        { label: 'Unit Handling', original: row?.handling, currency: row?.handlingCurrency, usd: row?.handlingUsd, date: row?.handlingDate },
        {
          label: 'Unit Debit Note',
          original: row?.debitNote,
          currency: row?.debitNoteCurrency,
          usd: row?.debitNoteUsd,
          date: row?.debitNoteDate,
          noteType: row?.debitNoteType,
          noteRemark: row?.debitNoteRemark,
        },
        {
          label: 'Unit Credit Note',
          original: row?.creditNote,
          currency: row?.creditNoteCurrency,
          usd: row?.creditNoteUsd,
          date: row?.creditNoteDate,
          noteType: row?.creditNoteType,
          noteRemark: row?.creditNoteRemark,
        },
      ];
  
    const totalExclStorage = costItems.reduce((sum, item) => {
      const value = item.label === 'Unit Credit Note' ? -(Number(item.usd) || 0) : (Number(item.usd) || 0);
      return sum + value;
    }, 0);

    const baseStorageCost = row?.baseStorageCost || 0;
    const baseStorageCostUsd = row?.baseStorageCostUsd || 0;
    const actualStorageCost = row?.actualStorageCost || 0;
    const storageLabel = row?.actualStoragePricing ? `Actual Storage Cost (Per ${row?.actualStoragePricing})` : 'Actual Storage Cost';
  
    // Storage cost per KG
    const storageCostPerKg = row?.actualStoragePricing === 'Carton' && row?.actualStorageCost && row?.shipmentRequestNetWeightPerCase
      ? row?.actualStorageCost / row?.shipmentRequestNetWeightPerCase
      : (row?.actualStorageCost || 0);
  
    // Total including storage per KG
    const totalInclStoragePerKg = totalExclStorage + storageCostPerKg;
  
    const formatAmt = (value, decimals = 4) =>
      new Intl.NumberFormat('en-US', { style: 'decimal', minimumFractionDigits: decimals }).format(value);
  
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Cost Details – SI {row?.finalShippingInstructionNumber || row?.id}</Typography>
            {row?.actualTimeOfArrival && (
              <Typography variant="subtitle2" color="textSecondary">
                ATA: {format(new Date(row?.actualTimeOfArrival), 'dd MMM yyyy')}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.100' }}>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell align="right"><strong>Amount</strong></TableCell>
                  <TableCell align="center"><strong>Currency</strong></TableCell>
                  <TableCell align="right"><strong>USD Amount</strong></TableCell>
                  <TableCell align="center"><strong>Date</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {costItems.map((item, idx) => (
                  <TableRow key={item.label} sx={{ backgroundColor: idx % 2 ? 'grey.50' : 'transparent' }}>
                    <TableCell>
                      {item.label}
                      {item.noteType && item.noteType !== ' - ' && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          Type: {item.noteType}
                        </Typography>
                      )}
                      {item.noteRemark && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          Remark: {item.noteRemark}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{item.original != null ? formatAmt(item.original) : '–'}</TableCell>
                    <TableCell align="center">{item.currency || '–'}</TableCell>
                    <TableCell align="right">{item.usd != null ? `${formatAmt(item.usd)} USD` : '–'}</TableCell>
                    <TableCell align="center">{item.date ? format(new Date(item.date), 'dd/MM/yyyy') : '–'}</TableCell>
                  </TableRow>
                ))}
                {/* Base Storage */}
                <TableRow sx={{ backgroundColor: costItems.length % 2 ? 'grey.50' : 'transparent' }}>
                  <TableCell>Base Storage Cost</TableCell>
                  <TableCell align="right">{formatAmt(baseStorageCost, 4)}</TableCell>
                  <TableCell align="center">Units</TableCell>
                  <TableCell align="right">{baseStorageCostUsd ? `${formatAmt(baseStorageCostUsd, 4)} USD` : '–'}</TableCell>
                  <TableCell align="center">–</TableCell>
                </TableRow>
                {/* Actual Storage */}
                <TableRow sx={{ backgroundColor: (costItems.length + 1) % 2 ? 'grey.50' : 'transparent' }}>
                  <TableCell>{storageLabel}</TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell align="right">{actualStorageCost ? `${formatAmt(actualStorageCost, 4)} USD` : '–'}</TableCell>
                  <TableCell />
                </TableRow>
                {/* Storage Per KG */}
                <TableRow sx={{ backgroundColor: (costItems.length + 2) % 2 ? 'grey.50' : 'transparent' }}>
                  <TableCell>Storage Cost Per KG</TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell align="right">{storageCostPerKg ? `${formatAmt(storageCostPerKg, 4)} USD` : '–'}</TableCell>
                  <TableCell />
                </TableRow>
                {/* Selling (exclude from totals) */}
                <TableRow>
                  <TableCell>Selling Price</TableCell>
                  <TableCell align="right">{row?.salesOrderFinalPrice != null ? formatAmt(row?.salesOrderFinalPrice) : '–'}</TableCell>
                  <TableCell align="center">{row?.salesOrderFinalPriceCurrency || '–'}</TableCell>
                  <TableCell align="right">{row?.salesOrderUsdFinalPrice != null ? `${formatAmt(row?.salesOrderUsdFinalPrice)} USD` : '–'}</TableCell>
                  <TableCell />
                </TableRow>
                {/* Summary */}
                <TableRow>
                  <TableCell sx={{ borderTop:1,borderColor:'grey.300' }}>
                    <Typography variant="subtitle1"><strong>Total (excl. Storage)</strong></Typography>
                  </TableCell>
                  <TableCell sx={{ borderTop:1,borderColor:'grey.300' }} />
                  <TableCell sx={{ borderTop:1,borderColor:'grey.300' }} />
                  <TableCell align="right" sx={{ borderTop:1,borderColor:'grey.300' }}>
                    <Typography variant="subtitle1">{`${formatAmt(totalExclStorage)} USD per KG`}</Typography>
                  </TableCell>
                  <TableCell sx={{ borderTop:1,borderColor:'grey.300' }} />
                </TableRow>
                <TableRow>
                  <TableCell sx={{ borderTop:1,borderColor:'grey.300' }}>
                    <Typography variant="subtitle1"><strong>Total (incl. Storage Per KG)</strong></Typography>
                  </TableCell>
                  <TableCell sx={{ borderTop:1,borderColor:'grey.300' }} />
                  <TableCell sx={{ borderTop:1,borderColor:'grey.300' }} />
                  <TableCell align="right" sx={{ borderTop:1,borderColor:'grey.300' }}>
                    <Typography variant="subtitle1">{`${formatAmt(totalInclStoragePerKg)} USD per KG`}</Typography>
                  </TableCell>
                  <TableCell sx={{ borderTop:1,borderColor:'grey.300' }} />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="outlined">Close</Button>
        </DialogActions>
      </Dialog>
    );
  }
  
  export default ViewShippingInstructionCostingDialog;
  